/*
  Cart page ("The Tab")
  ---------------------
  Three jobs, all scoped to [data-cart-page-root]:
    1. <cart-page-quick-add>  one-click gift wrap add that re-renders the page, the drawer and the header bubble.
    2. Sticky Total + Checkout bar on mobile, shown while the order summary is off screen.
    3. Complete Your Home Bar mirror: the app embed only mounts its block inside the drawer (the drawer exists on
       every page), so the page shows a live copy of that block and forwards clicks to the drawer's buttons.
  Plus: after a drawer-side change while on the cart page (cartUpdate from another source), the whole page root
  re-renders from the section so the summary never goes stale.
*/
(function () {
  'use strict';

  var ROOT_SEL = '[data-cart-page-root]';

  function root() {
    return document.querySelector(ROOT_SEL);
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function sectionId() {
    var el = document.getElementById('main-cart-items');
    return el ? el.dataset.id : null;
  }

  function publishCartUpdate(source) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: source }); } catch (e) { /* ignore */ }
    }
  }

  function afterRender() {
    if (typeof window.updateDeliveryEstimate === 'function') {
      try { window.updateDeliveryEstimate(); } catch (e) { /* ignore */ }
    }
    syncEmptyState();
    initSticky();
    mirrorHomeBar();
  }

  // Replace the page root from a rendered copy of this section.
  function renderRootFrom(html) {
    var current = root();
    var next = parseHTML(html).querySelector(ROOT_SEL);
    if (!current || !next) return false;
    current.innerHTML = next.innerHTML;
    afterRender();
    return true;
  }

  function renderDrawerFrom(sections) {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && sections['cart-drawer']) {
      var inner = drawer.querySelector('.drawer__inner');
      var src = parseHTML(sections['cart-drawer']).querySelector('.drawer__inner');
      if (inner && src) inner.innerHTML = src.innerHTML;
      var wrapper = document.querySelector('cart-drawer');
      if (wrapper) {
        var count = (src && src.querySelectorAll('.cart-item').length) || 0;
        wrapper.classList.toggle('is-empty', count === 0);
      }
    }
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var b = parseHTML(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (b) bubble.innerHTML = b.innerHTML;
    }
  }

  // Re-render the whole page from the live cart (used when the cart changed somewhere else).
  var refreshTimer = null;
  function refreshPage() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      var id = sectionId();
      if (!id) return;
      fetch(window.routes.cart_url + '?section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(renderRootFrom)
        .catch(function () { /* leave the page as it is */ });
    }, 60);
  }

  // ---------- 1. gift wrap quick add ----------
  if (!customElements.get('cart-page-quick-add')) {
    customElements.define('cart-page-quick-add', class CartPageQuickAdd extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.add.bind(this));
        this.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.add(e); }
        }.bind(this));
      }

      add(event) {
        event.preventDefault();
        if (this.classList.contains('is-loading')) return;
        this.classList.add('is-loading');
        var id = sectionId();
        var sections = ['cart-drawer', 'cart-icon-bubble'];
        if (id) sections.unshift(id);
        var body = {
          items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname
        };
        var self = this;
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            if (res.status && res.status >= 400) throw new Error(res.description || res.message || 'add failed');
            if (res.sections) {
              if (id && res.sections[id]) renderRootFrom(res.sections[id]);
              renderDrawerFrom(res.sections);
            }
            // 'cart-items' keeps assets/cart.js from fetching the section a second time; the Home Bar app still re-runs.
            publishCartUpdate('cart-items');
          })
          .catch(function (err) {
            self.classList.remove('is-loading');
            var errors = document.getElementById('cart-errors');
            if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || err.message;
          });
      }
    });
  }

  // ---------- empty state ----------
  function syncEmptyState() {
    var items = document.querySelector(ROOT_SEL + ' cart-items');
    if (!items) return;
    var empty = items.querySelectorAll('.cart-item').length === 0;
    items.classList.toggle('is-empty', empty);
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
    var checkout = document.getElementById('checkout');
    if (checkout) checkout.disabled = empty;
  }

  // ---------- 2. sticky checkout bar ----------
  var stickyObserver = null;
  function initSticky() {
    if (stickyObserver) { stickyObserver.disconnect(); stickyObserver = null; }
    var bar = document.querySelector('[data-cart-page-sticky]');
    var summary = document.querySelector('[data-cart-page-summary]');
    if (!bar || !summary || !('IntersectionObserver' in window)) return;
    var button = bar.querySelector('button');
    var set = function (visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (button) button.tabIndex = visible ? 0 : -1;
    };
    stickyObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      // Show while the summary is below the fold (or scrolled past), never while it is on screen.
      set(!entry.isIntersecting && document.querySelector(ROOT_SEL + ' cart-items:not(.is-empty)') !== null);
    }, { threshold: 0.15 });
    stickyObserver.observe(summary);
  }

  // ---------- 3. Complete Your Home Bar mirror ----------
  var suppressDrawerOpen = false;
  var suppressTimer = null;

  function sourceBox() {
    var drawer = document.getElementById('CartDrawer');
    return drawer ? drawer.querySelector('.home-bar') : null;
  }

  function mirrorHomeBar() {
    var mount = document.querySelector('[data-cart-page-homebar]');
    if (!mount) return;
    var src = sourceBox();
    var empty = !src || src.hidden || !src.innerHTML.trim();
    if (empty) {
      mount.hidden = true;
      mount.innerHTML = '';
      return;
    }
    var html = '<div class="home-bar" data-home-bar="cart-page">' + src.innerHTML + '</div>';
    if (mount.innerHTML !== html) mount.innerHTML = html;
    mount.hidden = false;
  }

  // Delegated from document: the mount is re-created on every page re-render, so no per-element listener.
  function forwardClick(event) {
    var btn = event.target.closest('.home-bar__add, .home-bar__opt');
    if (!btn) return;
    var mount = btn.closest('[data-cart-page-homebar]');
    if (!mount) return;
    var cls = btn.classList.contains('home-bar__add') ? '.home-bar__add' : '.home-bar__opt';
    var index = Array.prototype.indexOf.call(mount.querySelectorAll(cls), btn);
    var src = sourceBox();
    var target = src ? src.querySelectorAll(cls)[index] : null;
    if (!target) return;
    event.preventDefault();
    if (cls === '.home-bar__add') {
      // The app opens the drawer after an add; on the cart page the page itself updates instead.
      suppressDrawerOpen = true;
      clearTimeout(suppressTimer);
      suppressTimer = setTimeout(function () { suppressDrawerOpen = false; }, 4000);
    }
    target.click();
  }

  // The drawer is re-rendered by every cart change made outside this page (its own quantity buttons, the Home Bar
  // app, product forms). When its lines (variant + quantity) stop matching the page's lines, the page re-renders.
  // Changes made on the page re-render the drawer as well (assets/cart.js), so the two stay equal after those.
  function lineSignature(scope) {
    if (!scope) return '';
    return Array.prototype.map.call(scope.querySelectorAll('.cart-item .quantity__input'), function (i) {
      return i.dataset.quantityVariantId + ':' + i.value;
    }).join('|') + '|' + scope.querySelectorAll('.cart-item').length;
  }
  function drawerChanged() {
    var drawer = document.getElementById('CartDrawer');
    var page = root();
    if (!drawer || !page) return;
    if (lineSignature(drawer) !== lineSignature(page)) refreshPage();
  }

  function patchDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__cartPagePatched) return;
    var original = drawer.open;
    if (typeof original !== 'function') return;
    drawer.open = function () {
      if (suppressDrawerOpen) {
        suppressDrawerOpen = false;
        clearTimeout(suppressTimer);
        return;
      }
      return original.apply(this, arguments);
    };
    drawer.__cartPagePatched = true;
  }

  // ---------- wiring ----------
  var mirrorTimer = null;
  function scheduleMirror() {
    clearTimeout(mirrorTimer);
    mirrorTimer = setTimeout(mirrorHomeBar, 80);
  }

  function init() {
    if (!root()) return;

    document.addEventListener('click', forwardClick);

    if ('MutationObserver' in window) {
      // Drawer re-renders (quantity changes, Home Bar adds, ADDED states) -> refresh the mirror.
      var drawer = document.getElementById('CartDrawer');
      if (drawer) {
        new MutationObserver(function () {
          scheduleMirror();
          drawerChanged();
        }).observe(drawer, { childList: true, subtree: true, attributes: true, characterData: true });
      }
      // Page re-renders by assets/cart.js -> re-bind the sticky bar, mirror and empty state.
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var t = muts[i].target;
          if (t.closest && t.closest('[data-cart-page-homebar], [data-cart-timer], [data-cart-delivery-estimate], [data-cart-page-sticky]')) continue;
          clearTimeout(mirrorTimer);
          mirrorTimer = setTimeout(afterRender, 80);
          return;
        }
      }).observe(root(), { childList: true, subtree: true });
    }

    // A change made elsewhere (drawer, Home Bar app) while on the cart page: assets/cart.js already listens for
    // cartUpdate and calls onCartUpdate on <cart-items>; point that at a full page refresh so the summary,
    // timer and extras re-render too (its default only swaps the items and fetches the section by file name).
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-items').then(function () {
        var Cls = customElements.get('cart-items');
        if (Cls && Cls.prototype && !Cls.prototype.__cartPagePatched) {
          Cls.prototype.onCartUpdate = function () { refreshPage(); };
          Cls.prototype.__cartPagePatched = true;
        }
      }).catch(function () { /* ignore */ });
      customElements.whenDefined('cart-drawer').then(patchDrawerOpen).catch(function () { /* ignore */ });
    }

    afterRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
